import React, { useState } from 'react';
import styled from 'styled-components';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import {
  Button,
  Dialog,
  FILE_MANAGE_ORDER,
  FilesPanel,
  Select,
  type FileData,
  type SelectOption,
} from '../../../../../components';
import { PAGE_TEXT } from '../lang';
import { ProofDocumentType } from '../ProofDocument';
import {
  type AdditionalDocFormData,
  type AdditionalDocValue,
} from '../../../graphql';
import { type FormController, handleReadySubmit } from '../../../../../utils';
import { editAdditionalDocumentPermission } from './editAdditionalDocPermission';
import { Typography } from '@mui/material';
import { APPLICATION_MODULE } from '../constants';
import { APPLICATION_STATUS_MODULE } from '../../ApplicationStatus/constants';
import { useParams } from 'react-router-dom';

type AddDocsProps = {
  InitialAdditionalDocData: AdditionalDocFormData;
  enableEdit: FormController;
  addDocIndex: number;
  boxText: {
    subtitle: string;
    caption: string;
    filesPanel: {
      title: string;
      caption: string;
    };
  };
  docOptions: SelectOption[];
  enableDelete: boolean;
  handleSubmit: (values: AdditionalDocFormData) => Promise<void>;
  handleDelete: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const { PROOF_DOCUMENT } = PAGE_TEXT;
const ProofDocHelperText: Record<ProofDocumentType, string> = {
  [ProofDocumentType.PUBLIC]: PROOF_DOCUMENT.PUBLIC_EXPLANATION,
  [ProofDocumentType.INTERNAL]: PROOF_DOCUMENT.INTERNAL_EXPLANATION,
  [ProofDocumentType.RESTRICTED]: PROOF_DOCUMENT.RESTRICT_EXPLANATION,
};

export const AddDocBox: React.FunctionComponent<AddDocsProps> = (
  props: AddDocsProps,
) => {
  const {
    InitialAdditionalDocData,
    enableEdit,
    addDocIndex,
    boxText,
    docOptions,
    handleSubmit,
    handleDelete,
    handleLoading,
    enableDelete,
  } = props;

  const { caption, filesPanel } = boxText;

  const [openDialog, setOpenDialog] = useState(false);
  const [supportingDocuments, setSupportingDocuments] = useState<FileData[]>(
    InitialAdditionalDocData.supportingDocuments ?? [],
  );

  const { applicationId = '' } = useParams();

  const formik = useFormik<AdditionalDocValue>({
    initialValues: {
      documentType: InitialAdditionalDocData.documentType,
    },
    validationSchema: object({
      documentType: string().required('Required'),
    }),
    onSubmit: async values => {
      await handleSubmit({ ...values, supportingDocuments });
    },
  });

  const additionalDocPermission = editAdditionalDocumentPermission(enableEdit);

  const proofDocumentHelperText =
    ProofDocHelperText[formik.values.documentType as ProofDocumentType] ?? '';
  const proofDocumentCaption = proofDocumentHelperText.length > 0 && (
    <div className="flex flex-row items-start text-[#951D1D]">
      <NewReleasesIcon />
      <span className="italic ml-2">{proofDocumentHelperText}</span>
    </div>
  );

  function handleFilesChange(order: number, id: string): boolean {
    const newSupportingDocuments: FileData[] = [...supportingDocuments];
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD:
        setSupportingDocuments([
          ...newSupportingDocuments,
          {
            id,
            name: '',
            link: '',
          },
        ]);
        break;
      case FILE_MANAGE_ORDER.REMOVE:
        setSupportingDocuments(
          newSupportingDocuments.filter(doc => doc.id !== id),
        );
        break;
      default:
        break;
    }
    return true;
  }

  const handleCancelSubmit = (): void => {
    setOpenDialog(false);
  };
  const handleOpenDialog = (): void => {
    setOpenDialog(true);
  };
  const handleNavToMeetCondition = (): void => {
    window.location.href =
      '/application/' +
      applicationId +
      '?apsb=' +
      APPLICATION_MODULE.APPLICATION_PROGRESS +
      '&aphb=' +
      APPLICATION_STATUS_MODULE.CONDITIONAL_OFFER_CONFIRMATION;
  };

  return (
    <Container>
      <Dialog
        openDialog={openDialog}
        dialogTitle={''}
        dialogAction={PAGE_TEXT.GO_SUBMIT}
        cancelAction={PAGE_TEXT.BACK_TO_CHECK}
        maxWidth={'sm'}
        onDialogAction={handleNavToMeetCondition}
        onDialogClose={handleCancelSubmit}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PAGE_TEXT.QUALIFICATION.DIALOG_NOTE}
        </Typography>
      </Dialog>
      <ContentBox>
        <form onSubmit={handleReadySubmit(formik, addDocIndex)}>
          <div className="flex flex-row p-4">
            <div className="flex flex-col w-full pt-4 pl-2 pr-4">
              <Select
                value={formik.values.documentType}
                disabled={!additionalDocPermission.documentType}
                onChange={formik.handleChange}
                fullWidth
                label={caption}
                name={'documentType'}
                InputLabelProps={{ shrink: true }}
                options={docOptions}
                onBlur={formik.handleBlur}
                sx={{
                  marginBottom: '1rem',
                }}
                error={
                  formik.touched.documentType &&
                  Boolean(formik.errors.documentType)
                }
                helperText={
                  formik.touched.documentType && formik.errors.documentType
                }
              />
              {proofDocumentCaption}
            </div>
            <div className="mt-2">
              <FilesPanel
                disable={!additionalDocPermission.supportingDocuments}
                disableDeleteOnly={!enableDelete}
                title={filesPanel.title}
                caption={filesPanel.caption}
                files={supportingDocuments}
                handleFilesChange={handleFilesChange}
                handleLoading={handleLoading}
              />
            </div>
          </div>

          <div className="buttons-area">
            {additionalDocPermission.enableSave && (
              <Button
                type="submit"
                variant="contained"
                style={{ marginRight: '15px' }}
              >
                {PAGE_TEXT.SAVE}
              </Button>
            )}
            {additionalDocPermission.enableSaveCondition && (
              <Button
                variant="contained"
                onClick={handleOpenDialog}
                sx={{ marginLeft: '2rem' }}
              >
                {PAGE_TEXT.QUALIFICATION.CONDITION_MEET}
              </Button>
            )}
            {additionalDocPermission.enableDelete && enableDelete && (
              <Button variant="outlined" onClick={handleDelete}>
                {PAGE_TEXT.DELETE}
              </Button>
            )}
          </div>
        </form>
      </ContentBox>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    width: 100%;
  }

  .MuiFormControl-root {
    width: 100%;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    margin-bottom: 2rem;
    button {
      margin: 0 1rem;
    }
  }
`;

const ContentBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
`;
